import { Component, OnDestroy, ViewEncapsulation, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { toString as luceneToString, RangeExpression } from 'lucene';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, NgClass } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { OrderService } from '../../services/order.service';
import { WEEK_FORMAT_PATTERN, DATE_FORMAT_PATTERN } from '../../../shared/models/filter-formats';
import { CUSTOMER } from '../../../shared/models/settings';
import { UNDERSCORE } from '../../../shared/components/utils/constants';
import { ORDER_NUMBER_OPTIONS } from '../../../shared/models/list';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';

interface OrderInformation {
	customerOrderNumber: string;
	customerOrderItemNumber: string;
	cddWeek?: string;
	rddWeek: string;
	grossFirstBatchInStock: string;
	grossLastBatchInStock: string;
	supplierOrderNumber: string;
	supplierOrderItemNumber: string;
	isDelayed: boolean;
	isOnTime: boolean;
}

@Component({
	selector: 'copo-information-on-delivery-date',
	templateUrl: './information-on-delivery-date.component.html',
	styleUrl: './information-on-delivery-date.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		TranslateModule,
		NgFor,
		MatIconModule,
		NgClass,
		RouterLink,

		MatButtonModule,
		LoadingSpinnerComponent,
		DatePipe
	]
})
export class InformationOnDeliveryDateComponent implements OnDestroy, OnInit {
	orders?: OrderInformation[];
	orderSub?: Subscription;
	isLoading = true;
	millCode?: string;
	millEmail: string[] = [];
	tolerence = 0.1;

	readonly UNDERSCORE = UNDERSCORE;
	orderDisplay: keyof typeof ORDER_NUMBER_OPTIONS = CUSTOMER;

	constructor(
		private readonly orderService: OrderService,
		private readonly router: Router
	) {}

	ngOnInit() {
		this.orderSub = this.orderService.getOrders(
			[
				'customerOrderNumber',
				'customerOrderItemNumber',
				'cddWeek',
				'rddWeek',
				'grossFirstBatchInStock',
				'grossLastBatchInStock',
				'supplierOrderNumber',
				'supplierOrderItemNumber',
				'grossQtyDelivered',
				'grossQtyAvailableInTransit',
				'grossQtyOrdered',
				'grossQtyInProduction',
				'cdd'
			],
			luceneToString(<RangeExpression> this.createDateRangeFilter('cddWeek')),
			1,
			10,
			new OrderBy('cddWeek', OrderByDirection.ASC),
			false
		).subscribe({
			next: res => {
				this.orders = res.results.map(order => {
					const lastBatchInStockWeek = moment(order.grossLastBatchInStock).format(WEEK_FORMAT_PATTERN);
					const cddWeek = this.checkCddWeekData(order.cddWeek);
					const currentWeek = moment().format(WEEK_FORMAT_PATTERN);
					const cdd = moment(order.cdd).format(DATE_FORMAT_PATTERN);
					const currentDate = moment().format(DATE_FORMAT_PATTERN);
					const sum = order.grossQtyInProduction + order.grossQtyAvailableInTransit + order.grossQtyDelivered;
					return {
						customerOrderNumber: order.customerOrderNumber,
						customerOrderItemNumber: order.customerOrderItemNumber,
						cddWeek: order.cddWeek,
						rddWeek: order.rddWeek,
						grossFirstBatchInStock: order.grossFirstBatchInStock,
						grossLastBatchInStock: order.grossLastBatchInStock,
						supplierOrderNumber: order.supplierOrderNumber,
						supplierOrderItemNumber: order.supplierOrderItemNumber,
						isDelayed:
							(Math.abs(sum - order.grossQtyOrdered) < this.tolerence
								&& lastBatchInStockWeek > cddWeek && currentWeek < cddWeek)
							|| (currentDate > cdd),
						isOnTime: currentWeek < cddWeek
							&& Math.abs(sum - order.grossQtyOrdered) < this.tolerence
							&& lastBatchInStockWeek <= cddWeek
					};
				});
				this.isLoading = false;
			},
			error: (err: unknown) => {
				console.error('error requesting batches datacenter', err);
				this.isLoading = false;
			}
		});
	}

	createDateRangeFilter(field: string) {
		return {
			field,
			term_min: moment(moment().subtract(14, 'd')).format(WEEK_FORMAT_PATTERN),
			term_max: moment(moment().add(14, 'd')).format(WEEK_FORMAT_PATTERN),
			inclusive: 'both'
		};
	}

	ngOnDestroy() {
		if (this.orderSub) {
			this.orderSub.unsubscribe();
		}
	}

	async viewDetails() {
		await this.router.navigateByUrl('/order-tracking/order/datacenter', {
			state: {
				filter: [
					{
						id: 'cdd',
						values: [
							{
								from: moment(moment().subtract(14, 'd')).format(DATE_FORMAT_PATTERN),
								to: moment(moment().add(14, 'd')).format(DATE_FORMAT_PATTERN)
							}
						]
					}
				]
			}
		});
	}

	getOrderName(order: OrderInformation) {
		return order.customerOrderNumber === ''
			? UNDERSCORE
			: `${ order.customerOrderNumber }/${ order.customerOrderItemNumber }`;
	}

	checkCddWeekData(cddWeek?: string) {
		if (!cddWeek || cddWeek === '1900-W01') {
			return '';
		}

		return moment(cddWeek).format(WEEK_FORMAT_PATTERN);
	}

	checkCddWeekDataWithDatePipe(cddWeek?: string) {
		return this.orderService.checkCddWeekData(cddWeek);
	}
}
